import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { CircleF, GoogleMap, MarkerF, useJsApiLoader } from '@react-google-maps/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { Controller, useForm } from 'react-hook-form';
import { InputActionMeta } from 'react-select';
import { z } from 'zod';

import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { env } from '@/env';
import { locationKeys } from '@/queries/locations';
import { ApiService } from '@/services/api-v2';
import { useUserStore } from '@/stores/user.store';

import { AsyncSelect } from '../ui/async-select';
import { Form } from '../ui/form';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { useToast } from '../ui/toaster';
import { LocationCreatableMultiSelectField, locationFormSchema, LocationInputField } from './location-form';

export default function LocationDrawer({
  open: openProp,
  onOpenChange,
  noTrigger = false,
  callback,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean, success?: boolean) => void;
  noTrigger?: boolean;
  callback?: (data: z.infer<typeof locationFormSchema>) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const mapRef = useRef<GoogleMap>(null);
  const timezone = useUserStore.getState().user?.timezone;
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: env.VITE_GOOGLE_MAPS_API_KEY || '',
    // The workspace payload carries no country, so the Maps region biases to AU.
    region: 'au',
  });

  const [open, setOpen] = useState(openProp ?? false);
  const addressToken = useMemo(() => crypto.randomUUID(), []);
  const [isLoadingUserTimezoneLocation, setIsLoadingUserTimezoneLocation] = useState(true);

  useEffect(() => {
    setOpen(openProp ?? false);
  }, [openProp]);

  const form = useForm<z.infer<typeof locationFormSchema>>({
    resolver: zodResolver(locationFormSchema),
    defaultValues: {
      address: '',
      name: '',
      status: true,
      emails: [],
      lat: -26,
      lng: 135,
      placeId: '',
      radius: 15,
      zoom: 4,
    },
  });

  const { setValue } = form;

  useEffect(() => {
    setIsLoadingUserTimezoneLocation(true);
    if (timezone && open) {
      getUserTimezoneLocation(timezone || '', addressToken).then((location) => {
        if (location) {
          setValue('lat', location.lat);
          setValue('lng', location.lng);
        }
        setIsLoadingUserTimezoneLocation(false);
      });
    } else {
      setIsLoadingUserTimezoneLocation(false);
    }
  }, [timezone, addressToken, open, setValue]);

  const address = form.watch('address');
  const lat = form.watch('lat');
  const lng = form.watch('lng');
  const radius = form.watch('radius');
  const zoom = form.watch('zoom');
  const { mutateAsync: fetchAddressDetails } = useMutation({
    mutationKey: ['address-details'],
    mutationFn: async ({ value, token }: { value: string; token: string }) => await (await ApiService.GetAddress({ id: value, token })).json(),
    gcTime: 0,
  });

  const { mutateAsync: createLocation } = useMutation({
    mutationFn: (values: z.infer<typeof locationFormSchema>) => {
      return ApiService.CreateLocation({
        name: values.name,
        status: values.status,
        emails: values.emails.map((email) => email.value),
        address: values.address,
        placeId: values.placeId,
        radius: values.radius,
        zoom: values.zoom,
        lat: values.lat,
        lng: values.lng,
      });
    },
    onSuccess: async () => {
      await queryClient.resetQueries({
        queryKey: locationKeys.all,
      });
      handleOpenChange(false, true);
      callback?.(form.getValues());
      toast.success('Location added successfully');
    },
    onError: (error) => {
      toast.error(`Error adding location: ${error.message}`);
    },
  });

  const handleOpenChange = (open: boolean, success?: boolean) => {
    onOpenChange?.(open, success);
    setOpen(open);

    if (!open) form.reset();
  };

  const handleSubmit = async (data: z.infer<typeof locationFormSchema>) => {
    await createLocation(data);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        {!noTrigger && (
          <SheetTrigger asChild>
            <Button className="h-full rounded-none p-2" variant="success">
              Create Location
            </Button>
          </SheetTrigger>
        )}
        <SheetContent>
          <Form {...form}>
            <form className="flex h-full flex-col" onSubmit={form.handleSubmit(handleSubmit)}>
              <SheetHeader>
                <SheetTitle>Create Location</SheetTitle>
              </SheetHeader>
              <div className="flex flex-col gap-2 overflow-y-auto px-6 pt-4 pb-6">
                <div className="grid grid-cols-1 gap-2">
                  <LocationInputField label="Name" name="name" control={form.control} />
                  <Controller
                    control={form.control}
                    name="address"
                    render={({ field: { value, onChange } }) => (
                      <div className="flex flex-col gap-1">
                        <Label>Address</Label>
                        <LocationAddressSearch
                          token={addressToken}
                          value={value ? { label: value, value, secondary: '' } : undefined}
                          onChange={async (value) => {
                            onChange(value.label);
                            const result = await fetchAddressDetails({ value: value.value, token: addressToken });
                            form.setValue('placeId', value.value);
                            form.setValue('lat', result.address?.geometry?.lat ?? 0);
                            form.setValue('lng', result.address?.geometry?.lng ?? 0);
                            form.setValue('address', [value.label, result.address?.city, result.address?.state].join(', '));
                            form.setValue('zoom', 20);
                          }}
                        />
                      </div>
                    )}
                  />

                  <div className="relative h-[400px] w-full">
                    {isLoaded && (
                      <GoogleMap
                        ref={mapRef}
                        mapContainerStyle={{ height: '100%', width: '100%' }}
                        center={{ lat: lat ?? 0, lng: lng ?? 0 }}
                        zoom={zoom}
                        options={{
                          mapTypeControl: true,
                          mapTypeControlOptions: {
                            style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
                            position: google.maps.ControlPosition.TOP_RIGHT,
                          },
                          draggableCursor: 'crosshair',
                          zoomControl: false,
                          scaleControl: false,
                          streetViewControl: false,
                          fullscreenControl: false,
                        }}
                        onClick={(e: google.maps.MapMouseEvent) => {
                          if (!e.latLng) return;
                          form.setValue('lat', e.latLng.lat());
                          form.setValue('lng', e.latLng.lng());
                        }}>
                        {!!address && !!lat && !!lng && (
                          <MarkerF
                            position={{
                              lat: lat,
                              lng: lng,
                            }}
                          />
                        )}
                        {!!lat && !!lng && !!radius && (
                          <CircleF
                            center={{
                              lat: lat,
                              lng: lng,
                            }}
                            options={{
                              strokeColor: '#2E81DA',
                              strokeOpacity: 0.8,
                              strokeWeight: 2,
                              fillColor: '#2E81DA',
                              fillOpacity: 0.35,
                              clickable: false,
                              draggable: false,
                              editable: false,
                              visible: true,
                              radius: form.watch('radius'),
                              zIndex: 1,
                            }}
                          />
                        )}
                      </GoogleMap>
                    )}

                    {(!isLoaded || isLoadingUserTimezoneLocation) && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center bg-white opacity-100">
                        <span className="border-default-blue size-6 animate-spin rounded-full border-2 border-t-transparent" />
                      </div>
                    )}
                  </div>

                  <Controller
                    control={form.control}
                    name="radius"
                    render={({ field: { value, onChange } }) => (
                      <div className="flex flex-col gap-1">
                        <Label>Radius</Label>
                        <Slider min={10} max={300} value={value ? [value] : undefined} onValueChange={(value) => onChange(value[0])} />
                      </div>
                    )}
                  />

                  <LocationCreatableMultiSelectField label="Contact emails" name="emails" control={form.control} defaultOptions={[]} />
                </div>
              </div>

              <SheetFooter>
                <Button variant="grey" type="button" onClick={() => handleOpenChange(false)} disabled={form.formState.isSubmitting}>
                  Cancel
                </Button>
                <Button variant="success" type="submit" disabled={form.formState.isSubmitting}>
                  Save
                </Button>
              </SheetFooter>
            </form>
          </Form>
        </SheetContent>
      </Sheet>
    </>
  );
}

const debouncedSearchAddress = debounce(
  (
    inputValue: string,
    token: string,
    callback: (
      error: Error | null,
      result: Array<{
        label: string;
        secondary: string;
        value: string;
      }>,
    ) => void,
  ) => {
    ApiService.SearchAddresses({ query: inputValue, token })
      .then(async (response) => await response.json())
      .then((data) => {
        const options = data.addresses.map((result) => ({
          label: result.address.main_text,
          secondary: result.address.secondary_text,
          value: result.id,
        }));
        callback(null, options);
      })
      .catch((error: Error) => callback(error, []));
  },
  300,
);

async function getUserTimezoneLocation(timezone: string, addressToken: string) {
  const tzSplit = timezone.split('/');

  const tzPlaces = await (await ApiService.SearchAddresses({ query: `${tzSplit[1]}, ${tzSplit[0]}`, token: addressToken })).json();
  if (tzPlaces.addresses.length > 0) {
    const location = await (await ApiService.GetAddress({ id: tzPlaces.addresses[0].id, token: addressToken })).json();
    return { lat: location.address?.geometry?.lat ?? 0, lng: location.address?.geometry?.lng ?? 0 };
  }
  return null;
}

function LocationAddressSearch({
  value,
  onChange,
  disabled,
  token,
}: {
  value?: { label: string; value: string; secondary: string };
  onChange?: (address: { label: string; value: string; secondary: string }) => void;
  disabled?: boolean;
  token: string;
}) {
  const [inputValue, setInputValue] = useState('');
  const [menuIsOpened, setMenuIsOpened] = useState(false);
  const loadOptions = (inputValue: string, callback: (options: Array<{ label: string; value: string; secondary: string }>) => void) => {
    if (!inputValue) {
      return callback([]);
    }
    debouncedSearchAddress(inputValue, token, (error, results) => {
      if (error) {
        callback([]);
        return;
      }
      callback(results.map(({ label, value, secondary }) => ({ label, value, secondary })));
    });
  };

  const handleMenuOpen = () => {
    setMenuIsOpened(true);
    setInputValue(value?.label || '');
  };

  const displayValueProps = menuIsOpened ? { inputValue } : { value: { label: value?.label || '', value: value?.value || '', secondary: '' } };

  return (
    <AsyncSelect<{ label: string; value: string; secondary: string }>
      isDisabled={disabled}
      loadOptions={loadOptions}
      onChange={(newValue) => {
        if (newValue) {
          onChange?.(newValue);
          setInputValue(newValue.label);
        }
      }}
      onInputChange={(value, actionMeta: InputActionMeta) => {
        if (actionMeta.action !== 'input-blur' && actionMeta.action !== 'menu-close') {
          setInputValue(value);
        }
      }}
      formatOptionLabel={({ label, secondary }: { label: string; secondary?: string }) => (
        <div>
          <div>{label}</div>
          <div className="text-[#b9b9c3]">{secondary}</div>
        </div>
      )}
      {...displayValueProps}
      onMenuClose={() => setMenuIsOpened(false)}
      onMenuOpen={handleMenuOpen}
      placeholder="Search for an address..."
    />
  );
}
